import { useEffect, useRef, useState } from 'react'
import type { Navigate } from '../App'
import { useIsland, useSettings, useStore } from '../state/store'
import { useRecorder } from '../state/useRecorder'
import { speak, cancelSpeech } from '../lib/speech'
import { ToneReference } from '../components/ToneRibbon'
import { SpeakButton } from '../components/SpeakButton'
import { languageOf } from '../lib/languages'

/**
 * Shadowing practice. Playback loops one block at a time so you can speak over
 * it repeatedly; the visibility toggles let you strip away pinyin and English
 * as the block settles, which is the actual progression here.
 */
export function ListenDrill({ id, navigate }: { id: string; navigate: Navigate }) {
  const island = useIsland(id)
  const settings = useSettings()
  const { dispatch } = useStore()
  const record = useRecorder()

  const [index, setIndex] = useState(0)
  const [looping, setLooping] = useState(false)
  // Persisted rather than local: a pace you had to hunt for should not be
  // thrown away the moment you leave the drill.
  const rate = settings.speechRate
  const setRate = (value: number) =>
    dispatch({ type: 'updateSettings', patch: { speechRate: value } })
  const startedAt = useRef(Date.now())
  const loopRef = useRef(false)
  loopRef.current = looping

  const lang = languageOf(island?.language)
  const block = island?.blocks[index]

  // Looping re-speaks the same block until it is switched off or you move on.
  useEffect(() => {
    if (!looping || !block) return
    let cancelled = false

    const play = () => {
      if (cancelled || !loopRef.current) return
      const ok = speak(block.hanzi, {
        speech: lang.speech,
        voiceURI: settings.voiceByLanguage?.[lang.code] ?? null,
        rate,
        onEnd: () => {
          if (cancelled || !loopRef.current) return
          window.setTimeout(play, 700)
        },
        onUnavailable: () => setLooping(false),
      })
      if (!ok) setLooping(false)
    }

    play()
    return () => {
      cancelled = true
      cancelSpeech()
    }
  }, [looping, block, rate, settings.voiceURI])

  useEffect(() => () => cancelSpeech(), [])

  if (!island || !block) {
    return (
      <div className="empty">
        Nothing to play.{' '}
        <button type="button" className="btn is-small" onClick={() => navigate({ view: 'islands' })}>
          Back to islands
        </button>
      </div>
    )
  }

  const finish = () => {
    cancelSpeech()
    // Listening is logged but deliberately does not move the review schedule.
    record({
      islandId: island.id,
      mode: 'listen',
      score: 0,
      seconds: (Date.now() - startedAt.current) / 1000,
    })
    navigate({ view: 'island', id: island.id })
  }

  const move = (delta: number) => {
    setLooping(false)
    cancelSpeech()
    setIndex((i) => Math.min(island.blocks.length - 1, Math.max(0, i + delta)))
  }

  return (
    <>
      <button
        type="button"
        className="btn is-quiet is-small"
        onClick={finish}
        style={{ marginBottom: '0.8rem' }}
      >
        ← Done listening
      </button>

      <header className="page-head">
        <div className="eyebrow">
          Listen · {island.title} · block {index + 1} of {island.blocks.length}
        </div>
        <h1 className="page-title">
          <span className="page-title-zh">{lang.ui.listen}</span> Shadow it
        </h1>
        <p className="lede">
          Play it, speak over the top of it, and drop the crutches as it settles. The aim is to
          match the rhythm, not just the words.
        </p>
      </header>

      <div className="stage">
        <div className="hanzi hanzi-stage">{block.hanzi}</div>
        {lang.hasPhonetics && settings.showPinyinInListen && (
          <div className="pinyin" style={{ fontSize: '1rem', marginTop: '0.5rem' }}>
            {block.pinyin}
          </div>
        )}
        {lang.hasPhonetics && <ToneReference pinyin={block.pinyin} />}
        {settings.showEnglishInListen && (
          <p className="english" style={{ marginBottom: 0 }}>
            {block.english}
          </p>
        )}
        {block.note && (
          <p className="muted" style={{ fontSize: '0.86rem', marginBottom: 0 }}>
            {block.note}
          </p>
        )}
      </div>

      <div className="card">
        <div className="row">
          <SpeakButton
            text={block.hanzi}
            language={lang.code}
            className="btn is-primary"
            withSlow
          />
          <button
            type="button"
            className={`btn ${looping ? 'is-live' : ''}`}
            onClick={() => setLooping((v) => !v)}
          >
            {looping ? 'Stop loop' : 'Loop'}
          </button>

          <label className="row" style={{ marginLeft: 'auto', gap: '0.5rem' }}>
            <span className="stat-label">speed</span>
            <input
              type="range"
              min={0.3}
              max={1.3}
              step={0.05}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
            />
            <span className="data" style={{ fontSize: '0.8rem' }}>
              {rate.toFixed(2)}×
            </span>
          </label>
        </div>

        <div className="divider" />

        <div className="row">
          <label className="checkline">
            <input
              type="checkbox"
              checked={settings.showPinyinInListen}
              onChange={(e) =>
                dispatch({ type: 'updateSettings', patch: { showPinyinInListen: e.target.checked } })
              }
            />
            <span>Show pinyin</span>
          </label>
          <label className="checkline">
            <input
              type="checkbox"
              checked={settings.showEnglishInListen}
              onChange={(e) =>
                dispatch({
                  type: 'updateSettings',
                  patch: { showEnglishInListen: e.target.checked },
                })
              }
            />
            <span>Show English</span>
          </label>

          <span className="row" style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              className="btn is-small"
              onClick={() => move(-1)}
              disabled={index === 0}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn is-small"
              onClick={() => move(1)}
              disabled={index === island.blocks.length - 1}
            >
              Next block
            </button>
          </span>
        </div>
      </div>
    </>
  )
}
